import React, { useEffect, useMemo, useRef, useState } from "react";
import { CalendarRange, Check, ChevronDown } from "lucide-react";
import { Button, SegmentedControl, cx } from "../../pages/admin/_shared";
import { GRANULARITIES, TIME_RANGES, formatFullDate } from "./timeSeries";

/**
 * Giá trị khoảng thời gian:
 *  - { kind: "preset", days: 7 | 30 | 90 | "all" }
 *  - { kind: "custom", granularity: "day" | "week" | "month", from: "YYYY-MM-DD", to: "YYYY-MM-DD" }
 */
export const DEFAULT_RANGE = { kind: "preset", days: 30 };

/** Chuỗi query cho API /analytics/admin/traffic(…/series). */
export function rangeQuery(v) {
  if (v?.kind === "custom") return `from=${v.from}&to=${v.to}&granularity=${v.granularity}`;
  return `days=${v?.days ?? 30}`;
}

const pad2 = (n) => String(n).padStart(2, "0");
const keyOf = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const parse = (k) => {
  const [y, m, d] = k.split("-").map(Number);
  return new Date(y, m - 1, d || 1);
};
const todayKey = () => keyOf(new Date());

function weekStart(k) {
  const d = parse(k);
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  return keyOf(d);
}
function weekEnd(k) {
  const d = parse(weekStart(k));
  d.setDate(d.getDate() + 6);
  return keyOf(d);
}
function monthEnd(ym) {
  const [y, m] = ym.split("-").map(Number);
  return keyOf(new Date(y, m, 0));
}

function monthOptions(minKey) {
  const out = [];
  const start = parse(minKey || todayKey());
  const now = new Date();
  for (let d = new Date(start.getFullYear(), start.getMonth(), 1); d <= now; d.setMonth(d.getMonth() + 1)) {
    out.push({ value: `${d.getFullYear()}-${pad2(d.getMonth() + 1)}`, label: `Tháng ${d.getMonth() + 1}/${d.getFullYear()}` });
  }
  return out.reverse();
}

const short = (k) => {
  const [, m, d] = k.split("-");
  return `${d}/${m}`;
};

export function rangeSummary(v) {
  if (v?.kind !== "custom") return null;
  if (v.granularity === "month") {
    const a = v.from.slice(0, 7).split("-");
    const b = v.to.slice(0, 7).split("-");
    return a.join() === b.join() ? `Tháng ${Number(a[1])}/${a[0]}` : `T${Number(a[1])}/${a[0]} – T${Number(b[1])}/${b[0]}`;
  }
  return v.from === v.to ? formatFullDate(v.from) : `${short(v.from)} – ${formatFullDate(v.to)}`;
}

function addDaysKey(k, n) {
  const d = parse(k);
  d.setDate(d.getDate() + n);
  return keyOf(d);
}

/** Lối tắt hay dùng — tính theo giờ máy người xem (Việt Nam). */
function shortcuts(today) {
  const t = parse(today);
  const firstOfMonth = keyOf(new Date(t.getFullYear(), t.getMonth(), 1));
  const prevMonthStart = keyOf(new Date(t.getFullYear(), t.getMonth() - 1, 1));
  const prevMonthEnd = keyOf(new Date(t.getFullYear(), t.getMonth(), 0));
  const wk = weekStart(today);
  return [
    { label: "Hôm nay", from: today, to: today, granularity: "day" },
    { label: "Hôm qua", from: addDaysKey(today, -1), to: addDaysKey(today, -1), granularity: "day" },
    { label: "Tuần này", from: wk, to: today, granularity: "day" },
    { label: "Tuần trước", from: addDaysKey(wk, -7), to: addDaysKey(wk, -1), granularity: "day" },
    { label: "Tháng này", from: firstOfMonth, to: today, granularity: "day" },
    { label: "Tháng trước", from: prevMonthStart, to: prevMonthEnd, granularity: "day" },
    { label: "Năm nay", from: `${t.getFullYear()}-01-01`, to: today, granularity: "month" },
  ];
}

const inputCls =
  "h-9 w-full rounded-lg border border-admin-border bg-admin-surface px-2.5 text-sm text-admin-text focus:border-admin-primary focus:outline-none focus:ring-4 focus:ring-admin-primary/10";

/** Chọn nhanh 7/30/90 ngày/Từ đầu, hoặc tuỳ chọn từ ngày–ngày, tuần–tuần, tháng–tháng. */
export default function TrafficRangePicker({ value, onChange, minDate = "2026-04-15", align = "right" }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const today = todayKey();
  const custom = value?.kind === "custom";

  const [mode, setMode] = useState(custom ? value.granularity : "day");
  const [from, setFrom] = useState(custom ? value.from : minDate);
  const [to, setTo] = useState(custom ? value.to : today);
  const months = useMemo(() => monthOptions(minDate), [minDate]);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => rootRef.current && !rootRef.current.contains(e.target) && setOpen(false);
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Chuẩn hoá theo chế độ: tuần → thứ Hai…Chủ nhật; tháng → ngày 1…cuối tháng
  const resolved = useMemo(() => {
    let a = from <= to ? from : to;
    let b = from <= to ? to : from;
    if (mode === "week") {
      a = weekStart(a);
      b = weekEnd(b);
    } else if (mode === "month") {
      a = `${a.slice(0, 7)}-01`;
      b = monthEnd(b.slice(0, 7));
    }
    return { from: a < minDate ? minDate : a, to: b > today ? today : b };
  }, [from, to, mode, minDate, today]);

  const apply = () => {
    onChange({ kind: "custom", granularity: mode, ...resolved });
    setOpen(false);
  };

  const pickShortcut = (sc) => {
    const a = sc.from < minDate ? minDate : sc.from;
    onChange({ kind: "custom", granularity: sc.granularity, from: a, to: sc.to });
    setFrom(a);
    setTo(sc.to);
    setMode(sc.granularity);
    setOpen(false);
  };

  // Luôn hiện khoảng ngày thực tế đang xem trên nút — để người xem biết có thể bấm để chọn
  const shownRange = custom
    ? rangeSummary(value)
    : value?.days === "all"
      ? `${short(minDate)} – ${formatFullDate(today)}`
      : `${short(addDaysKey(today, -((Number(value?.days) || 30) - 1)))} – ${formatFullDate(today)}`;

  // Mở bảng chọn: điền sẵn khoảng đang xem
  const openPicker = () => {
    if (!open) {
      if (custom) {
        setFrom(value.from);
        setTo(value.to);
        setMode(value.granularity);
      } else {
        setFrom(value?.days === "all" ? minDate : addDaysKey(today, -((Number(value?.days) || 30) - 1)));
        setTo(today);
      }
    }
    setOpen((o) => !o);
  };

  const presetValue = custom ? null : value?.days;

  return (
    <div className="relative flex flex-wrap items-center gap-2" ref={rootRef}>
      <SegmentedControl
        ariaLabel="Khoảng thời gian"
        value={presetValue}
        onChange={(days) => onChange({ kind: "preset", days })}
        options={TIME_RANGES}
      />
      <button
        type="button"
        onClick={openPicker}
        aria-expanded={open}
        aria-haspopup="dialog"
        title="Chọn khoảng từ ngày nào đến ngày nào"
        className={cx(
          "inline-flex h-8 items-center gap-1.5 rounded-lg border px-2.5 text-xs font-semibold transition",
          custom
            ? "border-admin-primary bg-admin-primaryLight text-admin-primary"
            : "border-admin-border bg-admin-surface text-admin-text hover:bg-admin-muted"
        )}
      >
        <CalendarRange size={14} />
        <span className="tabular-nums">{shownRange}</span>
        <ChevronDown size={13} className={cx("transition", open && "rotate-180")} />
      </button>

      {open ? (
        <div
          role="dialog"
          aria-label="Chọn khoảng thời gian"
          className={cx(
            "absolute top-full z-40 mt-2 w-[340px] max-w-[calc(100vw-2rem)] rounded-xl border border-admin-border bg-admin-surface p-4 shadow-soft-xl",
            align === "right" ? "right-0" : "left-0"
          )}
          style={{ animation: "admin-pop-in 0.15s ease-out" }}
        >
          <p className="mb-2 text-xs font-semibold text-admin-textMuted">Chọn nhanh</p>
          <div className="mb-4 flex flex-wrap gap-1.5">
            {shortcuts(today).map((sc) => (
              <button
                key={sc.label}
                type="button"
                onClick={() => pickShortcut(sc)}
                className="rounded-full border border-admin-border bg-admin-surface px-2.5 py-1 text-xs font-medium text-admin-text transition hover:border-admin-primary hover:text-admin-primary"
              >
                {sc.label}
              </button>
            ))}
          </div>

          <p className="mb-2 text-xs font-semibold text-admin-textMuted">Hoặc chọn khoảng · xem theo</p>
          <SegmentedControl ariaLabel="Xem theo" value={mode} onChange={setMode} options={GRANULARITIES} className="w-full [&>button]:flex-1" />

          <div className="mt-4 grid grid-cols-2 gap-3">
            {mode === "month" ? (
              <>
                <label className="text-xs font-medium text-admin-textMuted">
                  Từ tháng
                  <select value={from.slice(0, 7)} onChange={(e) => setFrom(`${e.target.value}-01`)} className={cx(inputCls, "mt-1")}>
                    {months.map((m) => (
                      <option key={m.value} value={m.value}>
                        {m.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="text-xs font-medium text-admin-textMuted">
                  Đến tháng
                  <select value={to.slice(0, 7)} onChange={(e) => setTo(monthEnd(e.target.value))} className={cx(inputCls, "mt-1")}>
                    {months.map((m) => (
                      <option key={m.value} value={m.value}>
                        {m.label}
                      </option>
                    ))}
                  </select>
                </label>
              </>
            ) : (
              <>
                <label className="text-xs font-medium text-admin-textMuted">
                  {mode === "week" ? "Từ tuần (chọn 1 ngày)" : "Từ ngày"}
                  <input type="date" value={from} min={minDate} max={today} onChange={(e) => e.target.value && setFrom(e.target.value)} className={cx(inputCls, "mt-1")} />
                </label>
                <label className="text-xs font-medium text-admin-textMuted">
                  {mode === "week" ? "Đến tuần (chọn 1 ngày)" : "Đến ngày"}
                  <input type="date" value={to} min={minDate} max={today} onChange={(e) => e.target.value && setTo(e.target.value)} className={cx(inputCls, "mt-1")} />
                </label>
              </>
            )}
          </div>

          <p className="mt-3 rounded-lg bg-admin-muted px-3 py-2 text-xs text-admin-text">
            {formatFullDate(resolved.from)} → {formatFullDate(resolved.to)}
            {mode === "week" ? <span className="text-admin-textMuted"> · tính trọn tuần (thứ Hai – Chủ nhật)</span> : null}
            {mode === "month" ? <span className="text-admin-textMuted"> · tính trọn tháng</span> : null}
          </p>

          <div className="mt-4 flex justify-end gap-2">
            <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>
              Huỷ
            </Button>
            <Button size="sm" variant="primary" icon={Check} onClick={apply}>
              Áp dụng
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
